import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { LoginHero } from './LoginHero';
import { useSEO } from '../hooks/useSEO';
import {
  ArrowRight,
  FileText,
  Mail,
  CalendarCheck,
  Layers,
  Calculator,
  ShieldCheck,
  LineChart,
  Lock,
  KeyRound,
  EyeOff,
  Server,
  UserX,
  FileLock2,
  Sparkles,
  Banknote,
  ReceiptText,
  Users,
  CircleCheck,
  Briefcase,
  Landmark,
  ShieldPlus,
  FileBarChart,
  PieChart,
  UsersRound,
} from 'lucide-react';

/**
 * Landing page — extended layout:
 *   1. Single-viewport hero (unchanged visual) — now with larger body copy.
 *   2. "Your financial data. Handled with care." — trust / privacy section.
 *   3. "Everything EquiTrack does" — expanded feature grid with longer copy.
 *   4. Closing CTA.
 *
 * Copy tone is deliberately honest: we describe what we actually do (TLS,
 * bcrypt, Fernet-encrypted broker passwords, PII redaction, no data selling)
 * without over-promising "zero-knowledge" or "we can't see your data" — a
 * lie financial-data users would eventually notice and distrust.
 */

const FeatureTile = ({ icon: Icon, title, desc, isNew }) => (
  <div
    className="group flex items-start gap-3.5 p-3.5 rounded-lg transition-colors duration-200 hover:bg-[#F4F3EE]"
  >
    <div
      className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-[1.06]"
      style={{ backgroundColor: '#EBF3F0' }}
    >
      <Icon className="h-[19px] w-[19px]" style={{ color: '#1B4332', strokeWidth: 1.8 }} />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="text-[15px] font-medium leading-snug" style={{ color: '#1C1917', fontFamily: 'Work Sans' }}>
          {title}
        </h3>
        {isNew && (
          <span
            className="inline-block text-[9px] font-semibold uppercase tracking-[0.2em] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: 'rgba(168,230,193,0.32)', color: '#1B4332' }}
          >
            New
          </span>
        )}
      </div>
      <p className="text-[13.5px] leading-relaxed" style={{ color: '#57534E' }}>
        {desc}
      </p>
    </div>
  </div>
);

const SecurityRow = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-4">
    <div
      className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: 'rgba(27,67,50,0.08)' }}
    >
      <Icon className="h-5 w-5" style={{ color: '#1B4332', strokeWidth: 1.7 }} />
    </div>
    <div className="min-w-0">
      <h4
        className="text-[16px] font-medium mb-1.5"
        style={{ color: '#1C1917', fontFamily: 'Work Sans' }}
      >
        {title}
      </h4>
      <p className="text-[14px] leading-relaxed" style={{ color: '#57534E' }}>
        {body}
      </p>
    </div>
  </div>
);

export const Landing = () => {
  useSEO({
    title: 'EquiTrack — Indian Portfolio Tracker · FIFO P&L · ITR Computation · Life Insurance Tracker',
    description: 'India\'s most complete portfolio + tax companion. 5-min refreshed NSE/BSE prices, strict FIFO LTCG/STCG, Comprehensive ITR computation (Old vs New regime + CFL waterfall + Schedule 112A/111A/CG/BP/80C), Life Insurance premium calendar with multi-PAN 80C attribution, F&O contract-note parsing, mutual-fund CAS, Advance Tax planner. 7-day free trial.',
    keywords: 'Indian stock portfolio tracker, FIFO profit loss, ITR computation, Old vs New regime, Section 87A, Section 112A, Section 111A, Schedule CG, Schedule BP, Schedule 80C, life insurance premium tracker, premium calendar PDF, multi-PAN 80C attribution, contract note parser, F&O ITR-3, mutual fund CAS, AMFI NAV, EquiTrack, equitrack.in',
    canonical: 'https://www.equitrack.in/',
  });
  return (
    <PublicLayout>
      <div style={{ backgroundColor: '#FAFAF7' }} data-testid="landing-page">
        {/* ====================================================================
            SECTION 1 — HERO
           ==================================================================== */}
        <section
          className="grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 px-4 md:px-8 lg:px-12 py-10 md:py-14 max-w-7xl mx-auto items-center"
          style={{ minHeight: 'calc(100vh - 112px)' }}
          data-testid="landing-hero"
        >
          <div
            className="hidden lg:block relative h-[560px] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(14,42,32,0.18)] order-1"
            data-testid="landing-hero-visual"
          >
            <LoginHero />
          </div>

          <div className="order-2">
            <a
              href="#tax-harvesting"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.2em] mb-5 transition-all hover:shadow-[0_4px_14px_rgba(27,67,50,0.25)] hover:-translate-y-[1px]"
              style={{
                background: 'linear-gradient(180deg,#0E2A20 0%,#1B4332 100%)',
                color: '#A8E6C1',
                border: '1px solid rgba(168,230,193,0.35)',
              }}
              data-testid="landing-hero-tax-spotlight"
            >
              <Sparkles className="h-3 w-3" strokeWidth={2} />
              <span>New · Tax Harvesting · Save up to ₹15,625/yr in LTCG tax</span>
              <ArrowRight className="h-3 w-3" strokeWidth={2.2} />
            </a>

            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-[1.05] mb-5"
              style={{ color: '#1C1917', fontFamily: 'Work Sans', letterSpacing: '-0.02em' }}
              data-testid="landing-headline"
            >
              Track every rupee.
            </h1>

            <p
              className="text-[17px] md:text-[18px] leading-relaxed max-w-xl mb-8"
              style={{ color: '#3F3E3A' }}
            >
              Strict FIFO tax accounting, AI contract-note ingestion, automated corporate
              actions and tax-ready statements — purpose-built for NSE, BSE and Indian
              mutual funds. Built by an Indian investor, for Indian investors.
            </p>

            <div className="flex items-center gap-3 mb-10 flex-wrap">
              <Link
                to="/register"
                data-testid="landing-cta-start-trial"
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-lg text-[15px] font-medium transition-all duration-200 hover:shadow-[0_8px_22px_rgba(27,67,50,0.18)] hover:-translate-y-[1px]"
                style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}
              >
                Start 7-day free trial
                <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
              </Link>
              <Link
                to="/login"
                data-testid="landing-cta-login"
                className="inline-flex items-center px-6 py-3 rounded-lg text-[15px] font-medium transition-all duration-200"
                style={{ color: '#1C1917', border: '1px solid #E5E5DF', backgroundColor: '#FFFFFF' }}
              >
                Sign in
              </Link>
              <span className="text-[13px] ml-1" style={{ color: '#6F6E68' }}>
                No credit card required · Cancel anytime
              </span>
            </div>

            {/* Feature grid — 2 columns, larger tiles. Tax Harvesting tile
                spans both columns and gets a darker treatment so the
                ₹1.25L value-prop is unmissable above the fold. */}
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2 max-w-2xl"
              data-testid="landing-features-grid"
            >
              <a
                href="#tax-harvesting"
                className="sm:col-span-2 group flex items-start gap-3.5 p-3.5 rounded-lg transition-all duration-200 hover:shadow-[0_8px_24px_rgba(14,42,32,0.22)] hover:-translate-y-[1px]"
                style={{
                  background: 'linear-gradient(180deg,#0E2A20 0%,#1B4332 100%)',
                  border: '1px solid rgba(168,230,193,0.22)',
                }}
                data-testid="landing-tax-harvesting-spotlight-tile"
              >
                <div
                  className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-[1.06]"
                  style={{ backgroundColor: 'rgba(168,230,193,0.16)' }}
                >
                  <Sparkles className="h-[19px] w-[19px]" style={{ color: '#A8E6C1', strokeWidth: 1.8 }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="inline-block text-[9px] font-semibold uppercase tracking-[0.2em] px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: 'rgba(168,230,193,0.18)', color: '#A8E6C1' }}
                    >
                      New
                    </span>
                    <h3 className="text-[15px] font-medium" style={{ color: '#FFFFFF', fontFamily: 'Work Sans' }}>
                      Tax Harvesting Engine
                    </h3>
                  </div>
                  <p className="text-[13.5px] leading-snug" style={{ color: '#C5D8CD' }}>
                    Shield up to <strong style={{ color: '#A8E6C1' }}>₹1.25 lakh of LTCG</strong> per year —
                    that&apos;s <strong style={{ color: '#A8E6C1' }}>₹15,625 in tax saved</strong> at 12.5%.
                    Auto-detect lots to harvest before 31 Mar — without selling a single long-term stock.
                  </p>
                  <span className="inline-flex items-center gap-1 text-[12px] font-medium mt-1.5" style={{ color: '#A8E6C1' }}>
                    See how it works
                    <ArrowRight className="h-3 w-3" strokeWidth={2} />
                  </span>
                </div>
              </a>
              <FeatureTile
                icon={LineChart}
                title="Strict FIFO tax accounting"
                desc="ITR-ready LTCG/STCG with grandfather NAV — no Excel jugaad."
              />
              <FeatureTile
                icon={FileText}
                title="AI contract-note parsing"
                desc="Drop a PDF — trades land in the right portfolio in seconds."
              />
              <FeatureTile
                icon={Mail}
                title="Email auto-import"
                desc="Forward broker emails to a unique drop address. Hands-off."
              />
              <FeatureTile
                icon={CalendarCheck}
                title="Corporate actions, auto-detected"
                desc="Bonus, split, dividend, results — auto-tracked daily."
              />
              <FeatureTile
                icon={Layers}
                title="Mutual Funds via CAS"
                desc="AMFI NAV matching, ISIN resolution, monthly sync."
              />
              <FeatureTile
                icon={Briefcase}
                title="Futures & Options (F&O)"
                desc="Schedule BP, contract-wise turnover for §44AB, ITR-3 ready."
              />
              <FeatureTile
                icon={Landmark}
                title="Fixed Income + Advance Tax"
                desc="FDs · PPF · NSC · Bonds · SGB — accruals into Schedule OS/EI/80C + auto advance-tax instalment reminders."
              />
              <FeatureTile
                icon={ShieldPlus}
                title="Life Insurance tracker"
                desc="Premium calendar (FY-wise PDF) · multi-PAN 80C attribution · maturity tracking · auto-mark past premiums paid."
                isNew
              />
              <FeatureTile
                icon={FileBarChart}
                title="Comprehensive ITR computation"
                desc="Old vs New regime side-by-side · §87A · CFL waterfall · Schedule 112A/111A/CG/BP/80C — one click."
                isNew
              />
              <FeatureTile
                icon={PieChart}
                title="Net Worth Statement"
                desc="Live, password-protected Wealth Statement aggregating Equity · MF · FI · Insurance · Cash — with 1M / 3M / 1Y / 2Y trend chart and AES-128 encrypted PDF export."
                isNew
              />
              <FeatureTile
                icon={UsersRound}
                title="Track Family Portfolio"
                desc="One login. Per-Member tiles for Self, Spouse, Children & Parents — each with their own Advance Tax, 80C/80D attribution, Net Worth filter and Tax Harvesting lens."
                isNew
              />
              <FeatureTile
                icon={Calculator}
                title="Tax-ready ITR exports"
                desc="Per-FY P/L + dividend ledger — one click, your CA will love you."
              />
            </div>

            <div
              className="mt-8 flex items-center gap-4 flex-wrap text-[12px] uppercase tracking-[0.18em]"
              style={{ color: '#6F6E68' }}
              data-testid="landing-trust-strip"
            >
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{
                  backgroundColor: 'rgba(27,67,50,0.08)',
                  color: '#1B4332',
                  border: '1px solid rgba(27,67,50,0.18)',
                  letterSpacing: '0.14em',
                }}
                data-testid="landing-trust-encrypted-backups"
              >
                <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                E2E-encrypted backups
              </span>
              <span className="h-1 w-1 rounded-full" style={{ backgroundColor: '#C8C4B8' }} />
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" style={{ color: '#1B4332', strokeWidth: 1.8 }} />
                PDF invoiced
              </span>
              <span className="h-1 w-1 rounded-full" style={{ backgroundColor: '#C8C4B8' }} />
              <span>NSE / BSE end-of-day prices</span>
              <span className="h-1 w-1 rounded-full" style={{ backgroundColor: '#C8C4B8' }} />
              <span>Razorpay subscriptions</span>
            </div>
          </div>
        </section>

        {/* ====================================================================
            SECTION 1.5 — TAX HARVESTING SPOTLIGHT
           ==================================================================== */}
        <section
          id="tax-harvesting"
          className="px-4 md:px-8 lg:px-12 py-14 md:py-20 scroll-mt-20"
          style={{ background: 'linear-gradient(180deg,#0E2A20 0%,#1B4332 100%)', color: '#F7F7F5' }}
          data-testid="landing-tax-harvesting-section"
        >
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
            <div>
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.2em] mb-5"
                style={{ backgroundColor: 'rgba(168,230,193,0.14)', color: '#A8E6C1', border: '1px solid rgba(168,230,193,0.3)' }}
              >
                <Sparkles className="h-3 w-3" strokeWidth={2} />
                New · Tax Harvesting Engine
              </span>
              <h2
                className="text-3xl md:text-4xl lg:text-[42px] font-medium tracking-tight leading-[1.1] mb-5"
                style={{ fontFamily: 'Work Sans', letterSpacing: '-0.015em' }}
              >
                Save up to <span style={{ color: '#A8E6C1' }}>₹15,625</span> in LTCG tax,
                <br />without selling a single stock.
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: '#D6E5DC' }}>
                EquiTrack scans every long-term gain in your portfolio and tells you
                exactly how much to harvest before March 31st to use the
                <strong style={{ color: '#FFFFFF' }}> ₹1.25 lakh LTCG exemption</strong> —
                shielding that much profit from the 12.5% LTCG rate (≈ ₹15,625 tax saved)
                while flagging short-term losses you can offset against gains in the
                same year. Carry-Forward Loss tracking + matched-ST FIFO override
                + PAN grouping — all built around Indian Income Tax rules.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  '1.25L LTCG exemption auto-tracked per FY',
                  'Carry-Forward Losses with 8-year expiry calendar',
                  'Matched-ST FIFO override — book the exact loss you want',
                  'PAN grouping — consolidate multiple portfolios under the same PAN into one unified view',
                  'End-of-day (EOD) price refresh after market close',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5 text-[15px]" style={{ color: '#E5EFEA' }}>
                    <CircleCheck className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#A8E6C1', strokeWidth: 2 }} />
                    {line}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-[15px] font-medium transition-colors"
                  style={{ backgroundColor: '#A8E6C1', color: '#0E2A20' }}
                  data-testid="landing-tax-cta"
                >
                  Start saving on taxes
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </Link>
                <span className="text-[12px] uppercase tracking-[0.2em]" style={{ color: '#A9C8B6' }}>
                  Free · 7-day trial
                </span>
              </div>
            </div>

            {/* Mock dashboard tile — the visual sells the feature without
                screenshotting the live UI (which would go stale). */}
            <div
              className="rounded-2xl p-5 md:p-6"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: '#A9C8B6' }}>
                Tax Harvesting · FY 2025-26
              </p>
              <h3 className="text-[28px] md:text-[32px] font-medium mb-1" style={{ fontFamily: 'Work Sans' }}>
                {/* Iter-196 — corrected the misleading "tax saved" framing.
                    ₹37,420 is the LTCG PROFIT shielded under the ₹1.25L
                    annual exemption; actual tax saved is 12.5% of that =
                    ≈ ₹4,678. Surfacing both numbers so the value-prop
                    stays compelling AND mathematically honest. */}
                <span style={{ color: '#A8E6C1' }}>₹37,420</span>
                <span className="text-[14px] ml-2 align-middle" style={{ color: '#A9C8B6' }}>LTCG shielded · ≈₹4,678 tax saved</span>
              </h3>
              <p className="text-[13px] mb-5" style={{ color: '#C5D8CD' }}>
                ₹87,580 of the ₹1,25,000 LTCG exemption still available — harvest before 31 Mar to use the rest.
              </p>
              <div className="space-y-3">
                {[
                  { sym: 'INFY', qty: 60, gain: 18420, kind: 'LT — book gain', up: true },
                  { sym: 'TCS', qty: 12, gain: 6940, kind: 'LT — book gain', up: true },
                  { sym: 'ZOMATO', qty: 200, gain: -4180, kind: 'ST — book loss to offset', up: false },
                ].map((r) => (
                  <div
                    key={r.sym}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    <div>
                      <p className="font-medium" style={{ color: '#FFFFFF' }}>{r.sym}</p>
                      <p className="text-[11px]" style={{ color: '#A9C8B6' }}>{r.qty} shares · {r.kind}</p>
                    </div>
                    <span
                      className="text-[14px] font-medium tabular-nums"
                      style={{ color: r.up ? '#A8E6C1' : '#E7A69A' }}
                    >
                      {r.up ? '+' : ''}₹{Math.abs(r.gain).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] mt-4" style={{ color: '#7E9588', fontStyle: 'italic' }}>
                Illustrative — your actual numbers will reflect end-of-day prices.
              </p>
            </div>
          </div>
        </section>
        <section
          className="px-4 md:px-8 lg:px-12 py-16 md:py-24"
          style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #EAEAE2', borderBottom: '1px solid #EAEAE2' }}
          data-testid="landing-security-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-16 items-start">
              <div>
                <span
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.2em] mb-6"
                  style={{
                    backgroundColor: 'rgba(27,67,50,0.08)',
                    color: '#1B4332',
                    border: '1px solid rgba(27,67,50,0.18)',
                  }}
                >
                  <Lock className="h-3 w-3" strokeWidth={2} />
                  Your data, locked down
                </span>
                <h2
                  className="text-3xl md:text-4xl lg:text-[44px] font-medium tracking-tight leading-[1.1] mb-5"
                  style={{ color: '#1C1917', fontFamily: 'Work Sans', letterSpacing: '-0.015em' }}
                >
                  Your financial data.
                  <br />
                  <span style={{ color: '#1B4332' }}>Handled with care.</span>
                </h2>
                <p
                  className="text-[16px] leading-relaxed mb-6"
                  style={{ color: '#3F3E3A' }}
                >
                  You're entrusting us with trade history, holdings, and tax data —
                  details that could matter for decades. We take that seriously. Here's
                  a straight, honest account of how we protect it.
                </p>
                <p
                  className="text-[14px] leading-relaxed"
                  style={{ color: '#6F6E68' }}
                >
                  No marketing theatre. No "zero-knowledge magic." Just the real
                  safeguards we've built, which you can verify any time you'd like.
                </p>
              </div>

              <div className="space-y-7">
                {/* Featured row — End-to-end encrypted backups. Visually
                    distinct from the rest because this is the strongest
                    privacy guarantee we offer (and one the user explicitly
                    asked us to surface prominently — financial-data context). */}
                <div
                  className="relative p-5 md:p-6 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: '#0E2A20',
                    color: '#F0EFE9',
                    boxShadow: '0 12px 40px -18px rgba(14, 42, 32, 0.55)',
                  }}
                  data-testid="landing-encrypted-backups-feature"
                >
                  <div
                    aria-hidden
                    className="absolute -top-16 -right-16 w-56 h-56 rounded-full"
                    style={{ backgroundColor: 'rgba(168, 230, 193, 0.08)', filter: 'blur(2px)' }}
                  />
                  <div className="relative flex items-start gap-4">
                    <div
                      className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(168, 230, 193, 0.18)' }}
                    >
                      <FileLock2 className="h-6 w-6" style={{ color: '#A8E6C1', strokeWidth: 1.7 }} />
                    </div>
                    <div className="min-w-0">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.18em] mb-2"
                        style={{
                          backgroundColor: 'rgba(168, 230, 193, 0.18)',
                          color: '#A8E6C1',
                          border: '1px solid rgba(168, 230, 193, 0.35)',
                        }}
                      >
                        <Lock className="h-3 w-3" strokeWidth={2.2} />
                        Your privacy
                      </span>
                      <h4
                        className="text-[18px] font-semibold mb-1.5"
                        style={{ color: '#F7F7F5', fontFamily: 'Work Sans' }}
                      >
                        End-to-end encrypted backups · Founder cannot read your data
                      </h4>
                      <p className="text-[14px] leading-relaxed" style={{ color: '#D5D3CB' }}>
                        Database backups produced from our admin dashboard are sealed with
                        a public key whose matching private key never lives on our servers
                        — it sits offline in the founder's password manager. Even an
                        admin downloading your data sees only ciphertext. The only way
                        to read a backup is to decrypt it locally with that private
                        key.{' '}
                        <strong style={{ color: '#F7F7F5' }}>
                          Translation: a server breach, a stolen laptop, or
                          a malicious-insider scenario all leave your portfolio
                          unreadable.
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>

                <SecurityRow
                  icon={Lock}
                  title="Encrypted in transit, encrypted at rest"
                  body="Every byte between your browser and our servers travels over TLS 1.2+. Your database records sit on MongoDB Atlas with disk-level AES-256 encryption and private-network-only access — we do not expose it to the public internet."
                />
                <SecurityRow
                  icon={KeyRound}
                  title="Passwords nobody can reverse"
                  body="Login passwords are hashed with bcrypt — a deliberately slow, one-way function. Even we can't recover your password; if you forget it, you reset it. The same applies to any broker-PDF passwords you choose to save — those are further encrypted with a server-side key before they ever hit the database."
                />
                <SecurityRow
                  icon={UserX}
                  title="Never sold. Never shared. Never for marketing."
                  body="Your trade history is not a product. We do not sell, license, rent, or share your portfolio data with anyone — not advertisers, not data brokers, not research firms. Only you see your portfolios."
                />
                <SecurityRow
                  icon={FileLock2}
                  title="AI parsing is stateless"
                  body="When you upload a contract note, we send the PDF's text to our AI provider under a no-training data-processing agreement. The file is not stored on their side, not used to train any model, and is discarded once parsing finishes. The extracted trades are saved only in your account."
                />
                <SecurityRow
                  icon={EyeOff}
                  title="Strict access controls — even internally"
                  body="Only a minimal set of engineering staff has production access, and every privileged action is logged. We never browse customer portfolios for curiosity. Support interventions require a documented user request, and we disclose the minimum data needed to help you."
                />
                <SecurityRow
                  icon={Server}
                  title="You own your data — and can leave at any time"
                  body="Export your entire portfolio to Excel from Reports → Backup any time. Cancel the subscription and your backup still works. Request full account deletion and we purge your data within 3 days of the grace window expiring."
                />
              </div>
            </div>

            <div
              className="mt-14 p-5 md:p-6 rounded-xl flex items-start gap-4"
              style={{ backgroundColor: '#F8F7F2', border: '1px solid #EAEAE2' }}
            >
              <ShieldCheck className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#1B4332', strokeWidth: 1.7 }} />
              <p className="text-[14px] leading-relaxed" style={{ color: '#3F3E3A' }}>
                <strong style={{ color: '#1C1917' }}>One last honest note.</strong>{' '}
                The live database is, by necessity, readable to the application that serves
                your dashboard — that's true of every SaaS. What we've made unreadable is
                everything that <em>leaves</em> the database: every backup, every export,
                every disaster-recovery snapshot is encrypted with a key only the founder
                holds, offline. Combined with strict internal access controls, audit
                logging, and a policy of never browsing customer data without an explicit
                support request, this gives you a genuinely strong privacy posture for
                a finance-grade product — and one you can verify any time.
              </p>
            </div>
          </div>
        </section>

        {/* ====================================================================
            SECTION 3 — EXPANDED FEATURES
           ==================================================================== */}
        <section
          className="px-4 md:px-8 lg:px-12 py-16 md:py-24"
          data-testid="landing-features-detail"
        >
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12 md:mb-16">
              <span
                className="inline-block text-[11px] font-semibold uppercase tracking-[0.2em] mb-4"
                style={{ color: '#1B4332' }}
              >
                Everything EquiTrack does
              </span>
              <h2
                className="text-3xl md:text-4xl lg:text-[44px] font-medium tracking-tight leading-[1.1] mb-4"
                style={{ color: '#1C1917', fontFamily: 'Work Sans', letterSpacing: '-0.015em' }}
              >
                A full-stack portfolio engine,
                <br />
                not another spreadsheet.
              </h2>
              <p className="text-[16px] leading-relaxed" style={{ color: '#3F3E3A' }}>
                Indian investors shouldn't have to stitch together six tools at tax time.
                EquiTrack handles the whole chain — from trade ingestion to tax exports —
                while keeping your data where you can always reach it.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: LineChart,
                  title: 'Strict FIFO — the way ITD actually expects',
                  body:
                    'Every buy and sell is matched first-in, first-out across portfolios, accounting for bonuses, splits, and grandfather-NAV (for pre-2018 holdings). LTCG, STCG, intra-day, and speculative P/L are all computed automatically, so your ITR Schedule 112A/111A is literally one click away.',
                },
                {
                  icon: FileText,
                  title: 'Contract-note ingestion, AI + native templates',
                  body:
                    'Upload contract notes from any Indian broker — Zerodha, Groww, Upstox, ICICI Direct, HDFC Securities, Kotak, Motilal, Angel One — and we extract every trade along with brokerage, STT, exchange charges and GST. Native templates parse the big brokers in milliseconds at zero AI cost; the rest fall back to a secure AI path you never have to think about.',
                },
                {
                  icon: Mail,
                  title: 'Email auto-import — hands-off tracking',
                  body:
                    'Each account gets a unique drop address. Forward (or auto-forward) broker confirmation emails and we parse the attachment, route it to the right portfolio by broker code, and drop each trade into a review queue for one-click approval. Brokers that email you a daily contract note essentially become a live feed.',
                },
                {
                  icon: CalendarCheck,
                  title: 'Corporate actions, automatically detected',
                  body:
                    'We scan public sources daily for bonuses, splits, dividends, rights issues and board meetings for every stock you hold. Adjustments post automatically to your holdings and FIFO lots so your cost basis stays correct forever — no manual "ex-date" entry, no silent errors at tax time.',
                },
                {
                  icon: Layers,
                  title: 'Mutual Funds via CAS upload',
                  body:
                    'Drop a CAMS or KFintech eCAS PDF (password = your PAN). We extract every folio, scheme and unit — ISIN-matched against AMFI — and sync daily NAVs for ~14,000 schemes. Your equity and MF portfolios live side-by-side in one consolidated view with unified P/L, day change and unrealized gains.',
                },
                {
                  icon: Briefcase,
                  title: 'Futures & Options — Schedule BP, done right',
                  body:
                    'Drop your F&O contract note PDF — we extract every leg with full charge breakdown automatically. Per-trade P/L, contract-wise turnover for §44AB tax audit threshold, intra-day equity speculative bucket, and a CA-ready Schedule BP PDF you can hand over for ITR-3 filing. Available on the Full plan.',
                },
                {
                  icon: Landmark,
                  title: 'Fixed Income + Advance Tax — the whole bank-side picture',
                  body:
                    'Track FDs, RDs, PPF, NSC, KVP, SCSS, SSY, taxable & tax-free bonds, 54EC bonds, Sovereign Gold Bonds, EPF, VPF — all in one place with FY-accrual interest, maturity-value projections, and TDS estimates. Auto-routes taxable interest into Schedule OS, exempt income into Schedule EI, and 80C contributions into the deduction line of your ITR. The Advance Tax engine aggregates equity STCG/LTCG + intraday + F&O business + dividends + FI interest across every portfolio, projects FY-end liability, and lays out the 4 quarterly instalments (15-Jun / Sep / Dec / Mar) with countdown timers. One-click record-paid challan tracking + an auto-refreshing WebCal subscription so reminders stay live in Google / Apple / Outlook Calendar as you trade.',
                },
                {
                  icon: ShieldPlus,
                  title: 'Life Insurance — premium tracking that pays for itself',
                  body:
                    'Track every life-insurance policy you own (term, endowment, ULIP, money-back, whole life) in one ledger. EquiTrack auto-generates the premium schedule for the full policy term and silently marks past-dated premiums as already-paid on day one — so a policy you bought a decade ago doesn\'t show up as overdue. Premium Calendar view groups every upcoming due-date by month with a one-click PDF download (locker-ready, branded, with Holder / Insurer / Policy / Method / UTR all on a single page). The Dashboard surfaces your "Next premium due" widget so you never miss a deadline. Mark-Paid captures the PAN claiming the Section 80C deduction for THIS payment — vital for multi-PAN families where the spouse, parent or HUF actually pays. The Comprehensive ITR card then attributes every paid premium to the correct PAN\'s 80C bucket automatically.',
                },
                {
                  icon: FileBarChart,
                  title: 'Comprehensive ITR computation — every schedule, both regimes',
                  body:
                    'A full year-end tax computation tile in your dashboard — not just a "download my P/L" CSV. EquiTrack runs both Old and New regime side-by-side (with Section 87A rebate, marginal relief, surcharge, cess) and tells you which regime saves you money. Schedule CG handles 112A (with grandfather NAV + ₹1 lakh LTCG exemption), 111A (STCG @ 15%), debt LTCG / STCG, SGB redemption, 54EC bond exemption. Schedule BP rolls up F&O business with §44AB audit threshold check. Schedule OS picks up dividend + FI interest. Schedule 80C aggregates insurance premiums (PAN-attributed) + PPF/NSC contributions + ELSS. CFL Waterfall applies Sections 70/71/74 carry-forward losses across heads in the correct ITD-mandated order. Multi-PAN attribution lets a HUF, spouse and self each see their own ITR draft from the same EquiTrack login. Download the entire computation as a CA-ready Excel + PDF packet.',
                },
                {
                  icon: Calculator,
                  title: 'Reports that your CA will actually thank you for',
                  body:
                    'Per-financial-year P/L statements, Schedule 112A / 111A exports, dividend ledger with TDS tags, realized/unrealized splits, stock-wise and portfolio-wise drill-downs. Export to Excel or PDF with one click — all figures reconcile to the rupee with your ITR.',
                },
                {
                  icon: ReceiptText,
                  title: 'PDF invoices, automatically emailed',
                  body:
                    'Every subscription payment produces a clean PDF invoice emailed to you with HSN code, transaction reference and itemised charges. No chasing receipts, no "I lost the invoice" at year-end.',
                },
                {
                  icon: Banknote,
                  title: 'Monthly billing, true proration on upgrades',
                  body:
                    'Choose how many portfolios you need and the tier that fits. Upgrade or downgrade anytime — we prorate the unused days and apply them to your new plan (no double-charging, no "you paid annual so tough luck"). Razorpay handles checkout; EquiTrack handles the rest.',
                },
                {
                  icon: Users,
                  title: 'One account, many portfolios — consolidated view',
                  body:
                    'Self, spouse, parents, HUF, kids — manage all of them under one login with per-portfolio privacy or a single consolidated dashboard for total wealth. Custom groupings let you carve family vs. trading vs. long-term buckets any way you like.',
                },
                {
                  icon: Sparkles,
                  title: 'Broker sample uploads — grows for you and everyone',
                  body:
                    'If your broker isn\'t natively supported, share one sample contract note (PII auto-redacted) and we build a template from it. Once shipped, every user with that broker benefits — no AI cost, instant parsing, forever. You\'ve made EquiTrack better for the whole community.',
                },
              ].map((f) => (
                <div
                  key={f.title}
                  className="p-6 rounded-xl transition-all duration-200 hover:shadow-[0_12px_32px_rgba(14,42,32,0.08)]"
                  style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAEAE2' }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#EBF3F0' }}
                    >
                      <f.icon className="h-5 w-5" style={{ color: '#1B4332', strokeWidth: 1.7 }} />
                    </div>
                    <div className="min-w-0">
                      <h3
                        className="text-[17px] font-medium mb-2"
                        style={{ color: '#1C1917', fontFamily: 'Work Sans' }}
                      >
                        {f.title}
                      </h3>
                      <p className="text-[14.5px] leading-relaxed" style={{ color: '#57534E' }}>
                        {f.body}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====================================================================
            SECTION 4 — CLOSING CTA
           ==================================================================== */}
        <section
          className="px-4 md:px-8 lg:px-12 py-20 md:py-24"
          style={{ backgroundColor: '#1B4332' }}
          data-testid="landing-cta-section"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2
              className="text-3xl md:text-4xl lg:text-[46px] font-medium tracking-tight leading-[1.1] mb-5"
              style={{ color: '#FFFFFF', fontFamily: 'Work Sans', letterSpacing: '-0.015em' }}
            >
              Stop wrestling spreadsheets at tax time.
            </h2>
            <p
              className="text-[17px] md:text-[18px] leading-relaxed mb-9 max-w-2xl mx-auto"
              style={{ color: 'rgba(255,255,255,0.78)' }}
            >
              Seven days free. Full access to every feature. No card up front, no auto-subscribe
              surprise — you choose a plan only if you find it worth it.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/register"
                data-testid="landing-cta-closing"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-[16px] font-medium transition-all duration-200 hover:shadow-[0_10px_28px_rgba(0,0,0,0.28)] hover:-translate-y-[1px]"
                style={{ backgroundColor: '#FFFFFF', color: '#1B4332' }}
              >
                Start 7-day free trial
                <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center px-6 py-3.5 rounded-lg text-[15px] font-medium transition-all duration-200"
                style={{ color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.35)' }}
              >
                See pricing
              </Link>
            </div>
            <div
              className="mt-10 flex items-center justify-center gap-5 flex-wrap text-[12px] uppercase tracking-[0.18em]"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              <span className="inline-flex items-center gap-1.5">
                <CircleCheck className="h-3.5 w-3.5" strokeWidth={2} />
                No credit card
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CircleCheck className="h-3.5 w-3.5" strokeWidth={2} />
                Cancel anytime
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CircleCheck className="h-3.5 w-3.5" strokeWidth={2} />
                Data export forever
              </span>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
};

export default Landing;
